/**
 * The plain (non-bioinformatics, non-`.box`) document formats the web app opens by itself, without
 * asking the OBserver which viewer to use.
 *
 * These files need no server-side resolution: the extension *is* the answer, the browser reads the
 * bytes straight from storage, and no OmicsBox object is ever loaded. They are also outside the
 * bioinformatics format catalogue, so the file-ontology Lambda tags them with neither an EDAM term
 * nor a format id — there is nothing for an ontology-keyed lookup to match on anyway.
 *
 * SOURCE OF TRUTH: this map. The OBserver's `IWebRawFormatViewerCreator` registry still owns the
 * bioinformatics formats (FASTA/VCF/GFF/… via `bioinfo-viewer`), which are content-sniffed and
 * genuinely need server-side resolution. The two sets are disjoint; a new plain format is added
 * here, not there.
 *
 * The values are format ids, not extensions: they are handed to the viewer verbatim (as
 * `objectInfo.type` → the `format` field of the `getApiUrl` payload) and must match the vocabulary
 * `html-viewer`'s `storage-document.ts` branches on.
 */
const FORMAT_BY_EXTENSION: Record<string, string> = {
  // Images the browser renders as-is.
  png: 'png',
  jpg: 'jpeg',
  jpeg: 'jpeg',
  gif: 'gif',
  webp: 'webp',
  bmp: 'bmp',
  svg: 'svg',
  pdf: 'pdf',
  // Plain text, shown verbatim. Everything the viewer does not recognise falls through to its text
  // renderer, so `log` maps onto the same id rather than earning its own.
  txt: 'txt',
  log: 'txt',
  // Markdown earns its own id: html-viewer renders it, with a toggle back to the plain text. An older
  // html-viewer that does not know `md` falls through to its text renderer, i.e. what `txt` gave.
  md: 'md',
  markdown: 'md',
  // NOTE: html/htm are deliberately absent. An OmicsBox HTML report's `omicsbox:` protocol links are
  // resolved through the viewer's connection manager, which on the storage path points at the
  // presigned URL rather than at the OBserver — the links would silently fail. Those files keep
  // going through /metadata, where a controller-backed viewer can serve them.
}

/**
 * Source code, scripts and text configuration: shown verbatim, exactly like a `.txt`. Grouped by kind
 * only to keep the list readable — every entry resolves to the same `txt` id.
 *
 * Kept out on purpose: csv/tsv and the other bioinformatics extensions, which the OBserver owns; and
 * `out`, which is as often a compiled binary (`a.out`) as a job's captured stdout.
 */
const SOURCE_TEXT_EXTENSIONS: ReadonlySet<string> = new Set(
  [
    // Analysis and scripting languages; R Markdown and Quarto are shown as their source.
    ['py', 'r', 'rmd', 'qmd', 'jl', 'm', 'pl', 'pm', 'rb', 'lua'],
    // Shells.
    ['sh', 'bash', 'zsh'],
    // Pipeline definitions (Nextflow, Snakemake, WDL, CWL) and their `nextflow.config`-style
    // settings. A raw `.cwl` file is not a `Workflow` object, so cwl-viewer has no claim on it.
    ['nf', 'smk', 'wdl', 'cwl', 'config'],
    // Compiled languages.
    ['c', 'h', 'cpp', 'cc', 'cxx', 'hpp', 'java', 'kt', 'scala', 'go', 'rs', 'cs'],
    // Web and query languages.
    ['js', 'mjs', 'jsx', 'ts', 'tsx', 'css', 'sql'],
    // Structured text and configuration.
    ['json', 'yaml', 'yml', 'toml', 'xml', 'ini', 'cfg', 'conf'],
    // Prose markup kept as source, and change sets.
    ['rst', 'tex', 'bib', 'diff', 'patch'],
  ].flat()
)

/**
 * The format id for a plain document, or `undefined` when the file is not one — in which case the
 * caller falls back to resolving it through the OBserver.
 *
 * @param storageKey storage key or path; may contain folders (e.g. `reports/2024/plot.png`)
 */
export function plainFileFormat(storageKey: string | undefined | null): string | undefined {
  if (!storageKey) {
    return undefined
  }
  // Parse the basename, not the whole key: a folder named `2024.reports.pdf` holding a file called
  // `notes` must not resolve to `pdf`.
  const basename = storageKey.substring(storageKey.lastIndexOf('/') + 1)
  const dot = basename.lastIndexOf('.')
  // `dot <= 0` also rejects dotfiles: `.gitignore` is a name, not a `gitignore` extension.
  if (dot <= 0) {
    return undefined
  }
  const extension = basename.substring(dot + 1).toLowerCase()
  // A compressed payload is excluded on purpose: the storage source flags `compressed` for `.gz`,
  // but the document renderer does not decompress, so `report.txt.gz` would render as mojibake.
  // Falling through to /metadata gives an honest "no viewer" instead.
  if (SOURCE_TEXT_EXTENSIONS.has(extension)) {
    return 'txt'
  }
  return FORMAT_BY_EXTENSION[extension]
}

/**
 * Whether a file should be offered as openable, folding in the plain documents the web app opens by
 * itself. Keeps the ontology tree authoritative for everything else, and preserves its "unknown ⇒
 * still attempt" posture.
 *
 * Needed because the tree currently answers `false` for some plain files: `edam:data_2968` ("Image")
 * and `edam:format_2330` ("Textual format") are published without `viewerSupported`, so a tagged PNG
 * would be greyed out and its double-click swallowed even though we can now open it. The tidier
 * long-term fix is to flag those terms in the ontology exporter and delete this shim.
 *
 * @param ontologyOpenable the tree's verdict — `undefined` means "no opinion"
 */
export function resolveOpenability(
  storageKey: string | undefined | null,
  ontologyOpenable: boolean | undefined
): boolean | undefined {
  if (plainFileFormat(storageKey)) {
    return true
  }
  return ontologyOpenable
}
